import { z } from 'zod';

// User role enum from backend
export type Role = 'USER' | 'ADMIN' | 'SUPER_ADMIN';

// Feature enum from backend
export enum Feature {
    COURSES = 'COURSES',
    MEAL_PLANNING = 'MEAL_PLANNING',
    SHOPPING_LISTS = 'SHOPPING_LISTS',
    PANTRY = 'PANTRY',
    JOURNAL = 'JOURNAL',
    MEAL_LOGGING = 'MEAL_LOGGING',
    RECIPES = 'RECIPES',
    COMMUNITY = 'COMMUNITY',
    ANALYTICS = 'ANALYTICS',
    ALL = 'ALL',
}

// Client interface with features
export interface Client {
    id: string;
    userId: string;
    firstName: string;
    lastName: string;
    phoneNumber?: string;
    address?: string;
    featureAccess?: { feature: Feature }[];
    emailNotifications?: boolean;
    smsNotifications?: boolean;
}

// User interface based on backend Prisma schema
export interface User {
    id: string;
    firstName?: string;
    lastName?: string;
    email: string;
    username?: string;
    role: Role;
    isActive: boolean;
    isVerified: boolean;
    client?: Client;
}

// Authentication response from backend
export interface AuthResponse {
    user: User;
    accessToken: string;
    refreshToken: string;
}

// Login form schema
export const loginSchema = z.object({
    email: z.email('Please enter a valid email address'),
    password: z.string().min(1, 'Password is required'),
});

export type LoginFormData = z.infer<typeof loginSchema>;

// Register form schema based on backend SelfRegisterDto
export const registerSchema = z
    .object({
        email: z.email('Please enter a valid email address'),
        password: z
            .string()
            .min(8, 'Password must be at least 8 characters')
            .regex(
                /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/,
                'Password must contain at least one uppercase letter, one lowercase letter, and one number',
            ),
        confirmPassword: z.string(),
        username: z.string().min(2, 'Username must be at least 2 characters'),
        firstName: z.string().min(1, 'First name is required'),
        lastName: z.string().min(1, 'Last name is required'),
        phone: z.string().optional(),
        address: z.string().optional(),
    })
    .refine((data) => data.password === data.confirmPassword, {
        message: "Passwords don't match",
        path: ['confirmPassword'],
    });

export type RegisterFormData = z.infer<typeof registerSchema>;

// Password reset schemas
export const requestPasswordResetSchema = z.object({
    email: z.email('Please enter a valid email address'),
});

export type RequestPasswordResetFormData = z.infer<typeof requestPasswordResetSchema>;

export const resetPasswordSchema = z
    .object({
        token: z.string().min(1, 'Reset token is required'),
        password: z
            .string()
            .min(8, 'Password must be at least 8 characters')
            .regex(
                /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/,
                'Password must contain at least one uppercase letter, one lowercase letter, and one number',
            ),
        confirmPassword: z.string(),
    })
    .refine((data) => data.password === data.confirmPassword, {
        message: "Passwords don't match",
        path: ['confirmPassword'],
    });

export type ResetPasswordFormData = z.infer<typeof resetPasswordSchema>;
